import { ReleaseCondition, ClassData, Assessment, StreamPost } from '../types';

interface StudentContext {
  studentId: string;
  classData: ClassData;
}

/**
 * Returns true if the student meets the release condition (or no condition is set).
 */
export const evaluateCondition = (
  condition: ReleaseCondition | undefined,
  { studentId, classData }: StudentContext
): boolean => {
  if (!condition) return true;

  switch (condition.type) {
    case 'date': {
      if (!condition.releaseDate) return true;
      return new Date() >= new Date(condition.releaseDate);
    }

    case 'grade_threshold': {
      if (!condition.assessmentId || condition.gradePercent === undefined) return true;
      const gradeKey = `${studentId}-${condition.assessmentId}`;
      const grade = classData.grades?.[gradeKey];
      if (!grade) return false;
      const assessment = classData.assessments.find(a => a.id === condition.assessmentId);
      if (!assessment || !assessment.outOf) return false;
      const raw = typeof grade.rawScore === 'string' ? parseFloat(grade.rawScore) : grade.rawScore;
      if (isNaN(raw)) return false;
      return (raw / assessment.outOf) * 100 >= condition.gradePercent;
    }

    case 'pathway_node': {
      if (!condition.pathwayId || !condition.nodeId) return true;
      const progressKey = `${studentId}-${condition.pathwayId}`;
      const progress = classData.pathwayProgress?.[progressKey];
      if (!progress) return false;

      // Check if the node is an assignment node that auto-completes via grade
      const pathway = classData.learningPathways?.find(p => p.id === condition.pathwayId);
      const node = pathway?.nodes.find(n => n.id === condition.nodeId);
      if (node?.type === 'assignment' && node.assessmentId) {
        const gradeKey = `${studentId}-${node.assessmentId}`;
        if (gradeKey in (classData.grades || {})) return true;
      }

      return progress.completedNodeIds.includes(condition.nodeId);
    }

    case 'assignment_submitted': {
      if (!condition.submissionPostId) return true;
      const submissions = classData.submissions || [];
      return submissions.some(
        s => s.postId === condition.submissionPostId &&
          (s.studentId === studentId || (s.groupMemberIds || []).includes(studentId))
      );
    }

    default:
      return true;
  }
};

/** Human-readable label for a condition */
export const conditionLabel = (condition: ReleaseCondition, classData: ClassData): string => {
  switch (condition.type) {
    case 'date': {
      if (!condition.releaseDate) return 'On a date';
      return `Available after ${new Date(condition.releaseDate).toLocaleDateString('en-CA', {
        month: 'short', day: 'numeric', year: 'numeric',
      })}`;
    }
    case 'grade_threshold': {
      const a = classData.assessments.find(a => a.id === condition.assessmentId);
      return `Score ≥ ${condition.gradePercent ?? 0}% on "${a?.title ?? 'assessment'}"`;
    }
    case 'pathway_node': {
      const pathway = classData.learningPathways?.find(p => p.id === condition.pathwayId);
      const node = pathway?.nodes.find(n => n.id === condition.nodeId);
      return `Complete "${node?.title ?? 'node'}" in pathway`;
    }
    case 'assignment_submitted': {
      const post = (classData.stream || []).find(p => p.id === condition.submissionPostId);
      // also check pathway nodes
      if (!post) {
        for (const pathway of classData.learningPathways || []) {
          const node = pathway.nodes.find(n => n.id === condition.submissionPostId);
          if (node) return `Submit "${node.title}"`;
        }
      }
      return `Submit "${post?.title ?? 'assignment'}"`;
    }
    default:
      return 'Condition';
  }
};
